import React from 'react';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import EnrollmentTag from 'components/common/enrollment/EnrollmentTag';
import { EnrollmentPeriod, EnrollmentType, ProductType } from 'types/enrollmentService';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import StageButton from 'components/common/enrollment/StageButton';
import { Button } from '@insurancemenu/imenu-components-lib';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { useHistory } from 'react-router-dom';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { observer } from 'mobx-react-lite';
import ProductLineTitle from 'components/common/general/ProductLineTitle';
import { createStyles } from './styles';
import RenewalPortfolioCard from './RenewalPortfolioCard';
import { getMaxRateCount, hasMultiProductsPortfolio } from '../utils';
import InstalledPortfolioCard from '../InstalledEnrollmentCard/InstalledPortfolioCard';

interface RenewalEnrollmentCardProps {
  enrollmentPeriod: EnrollmentPeriod;
  footer: JSX.Element | null;
  onContinue?: () => void;
}

const RenewalEnrollmentCard = observer(
  ({ enrollmentPeriod, footer, onContinue }: RenewalEnrollmentCardProps): JSX.Element => {
    const { id, installedProposal, effectiveDate, hasConfirmedProposal } = enrollmentPeriod;

    const classes = createStyles({ footer, hasConfirmedProposal });

    // Mobx store
    const {
      userStore: { userRoles },
      enrollmentCardStore: {
        choosePlan,
        confirmRenewalProposal,
        getPeriodLoading,
        getProductLineFromDefaultRenewalProposal,
        canConfirmDefaultProposal,
      },
    } = useRootStore();

    // Common data
    const history = useHistory();
    const convertedUserRoles = userRoles.map((role) => toJS(role)).slice();

    // Component actions
    const redirect = (url: string): void => {
      history.push(url);
    };

    const onContinueClick = (): void => {
      if (onContinue) {
        onContinue();
      } else {
        redirect(`/checklist/${enrollmentPeriod.id}`);
      }
    };

    const onRenewalAction = (productLineId: ProductLineId, action: ProductType): void => {
      choosePlan(id, productLineId, action);
    };

    const confirmPeriod = async (): Promise<void> => {
      await confirmRenewalProposal(id);
      redirect(`/checklist/${enrollmentPeriod.id}`);
    };

    const defaultChoice = (periodId: string, productLineId: ProductLineId): string | null => {
      try {
        return getProductLineFromDefaultRenewalProposal(id, productLineId).confirmedPortfolioId;
      } catch {
        return null;
      }
    };

    // Components
    const cardsList = installedProposal?.productLines
      .sort((a, b) => sortByProductLineId(a.id, b.id))
      .filter((productLineData) => productLineData.products.length > 0)
      .map((productLineData) => {
        return (
          <div className={classes.productLineSection} key={`${id}-${productLineData.id}`}>
            <ProductLineTitle productLineId={productLineData.id} styles={classes.productLineTitle} />
            <div className={classes.cardsWrapper}>
              <InstalledPortfolioCard
                productLineData={productLineData}
                maxRatesCount={getMaxRateCount(installedProposal?.productLines || [], EnrollmentType.RENEWAL)}
                hasMultiProductsPortfolio={installedProposal ? hasMultiProductsPortfolio(installedProposal) : false}
                effectiveDate={effectiveDate}
                showProductLine={false}
              />
              <RenewalPortfolioCard
                productLineData={productLineData}
                renewalAction={onRenewalAction}
                defaultChoice={defaultChoice(id, productLineData.id)}
                enrollmentPeriod={{ installedProposal, effectiveDate }}
              />
            </div>
          </div>
        );
      });

    const continueButton = (): JSX.Element => {
      return (
        <>
          {enrollmentPeriod.hasConfirmedProposal ? (
            <Button
              color="primary"
              size="small"
              width="18.4rem"
              className={classes.continueButton}
              onClick={onContinueClick}
            >
              Continue
            </Button>
          ) : (
            <Button
              color="primary"
              size="small"
              width="18.4rem"
              className={classes.continueButton}
              onClick={confirmPeriod}
              disabled={!canConfirmDefaultProposal(id)}
            >
              Confirm
            </Button>
          )}
        </>
      );
    };

    return (
      <div className={classes.enrollmentCard}>
        <FormsSpinner display={getPeriodLoading(id)} />
        <header className={classes.cardHeader}>
          <div className={classes.leftHeaderSection}>
            <div>
              <h6 className={classes.effectiveDateTitle}>Renewal Date</h6>
              <h5 className={classes.effectiveDateValue}>
                {dateFormatters.normDateToShortAmericanDate(effectiveDate)}
              </h5>
            </div>
            <div className={classes.stageTagContainer}>
              <EnrollmentTag userRoles={convertedUserRoles} enrollmentPeriod={enrollmentPeriod} />
            </div>
          </div>
          {continueButton()}
        </header>
        <footer className={classes.footer}>
          {enrollmentPeriod.hasConfirmedProposal && footer && (
            <div className={classes.proposalInfo}>
              {footer}
              <StageButton enrollmentPeriod={enrollmentPeriod} />
            </div>
          )}
          {cardsList && cardsList.length > 0 && !enrollmentPeriod.hasConfirmedProposal && (
            <div className={classes.portfoliosList}>{cardsList}</div>
          )}
        </footer>
      </div>
    );
  },
);

export default RenewalEnrollmentCard;
